<!-- Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. -->

<ng-container
	*ngIf="{
	conflictedTickets: conflictedTickets$ | async,
	resolvedTickets: resolvedTickets$ | async,
	selectedTabIndex: selectedTabIndex$ | async,
	embiggenedItem: embiggenedItem$ | async,
	hasLoaded: waitForInitialization$ | async,
	} as data"
>
	<form [formGroup]="formGroup" (ngSubmit)="onNext()">
		<mat-tab-group
			mat-dialog-content
			[selectedIndex]="data.selectedTabIndex"
			(selectedIndexChange)="selectedTabIndex$.next($event)"
		>
			<mat-tab [disabled]="!data.conflictedTickets?.length">
				<ng-template mat-tab-label>
					<mat-icon class="tab-icon" automationId="conflictsLabel">quick_reference</mat-icon>
					{{ appStrings.IMPORT_TICKET_CONFLICT_TAB }}
					<span *ngIf="data.conflictedTickets?.length as conflictedLength" automationId="conflictsNumber">
						({{ conflictedLength }})
					</span>
				</ng-template>
				<ng-container
					formArrayName="conflictArray"
					*ngFor="let formalTicket of data.conflictedTickets; trackBy: trackByTicketId"
				>
					<ng-container
						*ngTemplateOutlet="
							ticketItem;
							context: {
								formalTicket: formalTicket,
								currentOb: conflictedTickets$,
								currentForm: conflictArray,
								selectedTabIndex: data.selectedTabIndex,
								embiggenedItem: data.embiggenedItem
							}
						"
					></ng-container>
				</ng-container>
				<!-- null state -->
				<ng-container *ngIf="!data.resolvedTickets?.length">
					<div *ngIf="!data.hasLoaded" class="no-tickets"><mat-spinner></mat-spinner></div>
				</ng-container>
			</mat-tab>
			<mat-tab>
				<ng-template mat-tab-label>
					<mat-icon class="tab-icon" automationId="resolvedTickets">task_alt</mat-icon>
					{{ appStrings.IMPORT_TICKET_RESOLVED_TAB }}
					<span *ngIf="data.resolvedTickets?.length as resolvedLength" automationId="resolvedLength">
						({{ resolvedLength }})
					</span>
				</ng-template>
				<ng-container
					formArrayName="resolvedArray"
					*ngFor="let formalTicket of data.resolvedTickets; trackBy: trackByTicketId"
				>
					<ng-container
						*ngTemplateOutlet="
							ticketItem;
							context: {
								formalTicket: formalTicket,
								currentOb: resolvedTickets$,
								currentForm: resolvedArray,
								selectedTabIndex: data.selectedTabIndex,
								embiggenedItem: data.embiggenedItem
							}
						"
					></ng-container>
				</ng-container>
				<!-- null state -->
				<ng-container *ngIf="!data.resolvedTickets?.length">
					<div *ngIf="data.hasLoaded" class="no-tickets">{{ appStrings.IMPORT_TICKET_ALL_EXCLUDED }}</div>
					<div *ngIf="!data.hasLoaded" class="no-tickets"><mat-spinner></mat-spinner></div>
				</ng-container>
			</mat-tab>
		</mat-tab-group>

		<ng-template
			#ticketItem
			let-formalTicket="formalTicket"
			let-currentOb="currentOb"
			let-currentForm="currentForm"
			let-selectedTabIndex="selectedTabIndex"
			let-embiggenedItem="embiggenedItem"
		>
			<div [formGroup]="formalTicket.formGroup" class="item-grid">
				<secure-image
					class="row-image"
					[src]="formalTicket.imageUrl"
					[skeletonSrc]="DEFAULT_IMAGE_SKELETON"
					[alt]="formalTicket.name"
					(imageClick)="toggleImageBigness(formalTicket)"
					[ngClass]="{ embiggened: (embiggenedItem$ | async) === formalTicket }"
				/>
				<mat-form-field>
					<mat-label>{{ appStrings.TICKET_EDIT_DESCRIPTION }}</mat-label>
					<input type="text" matInput formControlName="activityName" automationId="editDescription" />
					<mat-error>
						{{ appStrings.TICKET_ERRORS_DESCRIPTION_REQUIRED_HEADER }}
					</mat-error>
				</mat-form-field>

				<mat-form-field>
					<mat-label>{{ appStrings.IMAGE_COLLECTION_PLAN_START_DATE_FIELD_LABEL }}</mat-label>
					<input
						matInput
						readonly
						[matDatepicker]="planStartDatePicker"
						automationId="planStartDateField"
						formControlName="plannedStart"
						(click)="planStartDatePicker.open()"
					/>
					<mat-datepicker-toggle
						automationId="planStartDatePicker"
						matSuffix
						[for]="planStartDatePicker"
					></mat-datepicker-toggle>
					<mat-datepicker touchUi #planStartDatePicker></mat-datepicker>
					<mat-error
						*ngIf="formalTicket.formGroup.get('plannedStart')?.hasError('required')"
						automationId="error"
					>
						{{ appStrings.IMPORT_TICKET_PLAN_START_REQUIRED }}
					</mat-error>
					<mat-error *ngIf="formalTicket.formGroup.get('plannedStart')?.hasError('activeLineSideValidator')">
						{{ appStrings.MUST_BE_BEFORE_ACTIVE_LINE() }}
					</mat-error>
				</mat-form-field>
				<mat-form-field>
					<mat-label>{{ appStrings.CREW }}</mat-label>
					<input
						type="number"
						matInput
						automationId="crewSize"
						formControlName="crewSize"
						[min]="inputValidationConfig.crewMin"
						[max]="inputValidationConfig.crewMax"
					/>
					<mat-error *ngIf="formalTicket.formGroup.get('crewSize')?.hasError('required')">
						{{ appStrings.GENERAL_REQUIRED_FIELD }}
					</mat-error>
					<mat-error
						*ngIf="
							formalTicket.formGroup.get('crewSize')?.hasError('min') ||
							formalTicket.formGroup.get('crewSize')?.hasError('max')
						"
					>
						{{
							appStrings.IMPORT_TICKET_BETWEEN_SMALL_ERROR(
								inputValidationConfig.crewMin,
								inputValidationConfig.crewMax
							)
						}}
					</mat-error>
				</mat-form-field>

				<!-- as is tradition, mat-extended-select doesn't properly handle the submit event -->
				<mat-extended-select
					formControlName="roleId"
					placeholder="Role"
					automationId="role"
					[isNotSetValid]="false"
					[required]="true"
					[includeFilter]="false"
					[option$]="roleList$"
					[labelPath]="['view', 'roleName']"
					[valuePath]="['$id']"
					[iconBackgroundColorPath]="['view', 'color']"
				></mat-extended-select>

				<mat-extended-select
					formControlName="locationId"
					placeholder="Location"
					automationId="location"
					[includeFilter]="false"
					[option$]="locationList$"
					[labelPath]="['view', 'name']"
					[valuePath]="['$id']"
					[iconBackgroundColorPath]="['view', 'color']"
					[required]="false"
				></mat-extended-select>

				<mat-form-field>
					<mat-label>{{ appStrings.DAYS }}</mat-label>
					<input
						type="number"
						matInput
						automationId="durationDays"
						formControlName="durationDays"
						[min]="inputValidationConfig.durationMin"
						[max]="inputValidationConfig.durationMax"
					/>
					<mat-error *ngIf="formalTicket.formGroup.get('durationDays')?.hasError('required')">
						{{ appStrings.GENERAL_REQUIRED_FIELD }}
					</mat-error>
					<mat-error
						*ngIf="
							formalTicket.formGroup.get('durationDays')?.hasError('min') ||
							formalTicket.formGroup.get('durationDays')?.hasError('max')
						"
					>
						{{
							appStrings.IMPORT_TICKET_BETWEEN_SMALL_ERROR(
								inputValidationConfig.durationMinForValidationText,
								inputValidationConfig.durationMax
							)
						}}
					</mat-error>
				</mat-form-field>

				<div class="grid-defying-bottom-row">
					<mat-label class="mat-subtitle-2">{{ appStrings.TICKET_TYPE }}:</mat-label>
					<mat-radio-group aria-label="Select an option" formControlName="type">
						<mat-radio-button value="task" automationId="task">{{
							appStrings.TICKET_TYPE_TASK
						}}</mat-radio-button>
						<mat-radio-button value="milestone" automationId="milestone">{{
							appStrings.TICKET_TYPE_MILESTONE
						}}</mat-radio-button>
					</mat-radio-group>

					<mat-divider vertical="true" class="padded-divider vertical-divider-height"></mat-divider>

					<mat-icon class="tab-icon">filter_center_focus</mat-icon>
					<span class="mat-subtitle-2" automationId="boardColumn">
						{{ appStrings.IMPORT_TICKET_BOARD_LABEL }}
						{{ formalTicket.ticket?.dataSourceMetadata?.boardIndex ?? 'x' }}
						{{ appStrings.IMPORT_TICKET_COLUMN_LABEL }}
						{{ formalTicket.ticket?.dataSourceMetadata?.boardColumn ?? 'x' }}</span
					>
					<button
						mat-button
						*ngIf="(selectedTabIndex$ | async) === 0"
						type="button"
						automationId="resolveGreen"
						class="green-override"
						(click)="resolveTicketFromConflictUi(formalTicket)"
					>
						<mat-icon>add_task</mat-icon>
						{{ appStrings.IMPORT_TICKET_RESOLVE_BUTTON }}
					</button>
					<button
						mat-button
						type="button"
						automationId="excludeWarning"
						color="warn"
						(click)="excludeTickets([formalTicket], currentOb, currentForm)"
					>
						<mat-icon>hide_source</mat-icon>
						{{ appStrings.IMPORT_TICKET_EXCLUDE_BUTTON }}
					</button>
				</div>
			</div>
			<mat-divider class="vertical-padded-divider"></mat-divider>
		</ng-template>

		<!-- Footer -->
		<button-footer mat-dialog-actions [saveOrCloseButtonText]="submitButtonText$ | async">
			<div
				leftButton
				class="mat-icon-with-text-silliness"
				*ngIf="(excludedTickets$ | async)?.length as excludedLength"
			>
				<mat-icon class="tab-icon">info</mat-icon>
				{{ appStrings.IMPORT_TICKET_EXCLUDED_COUNT(excludedLength) }}
			</div>
		</button-footer>
	</form>
</ng-container>
